import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';

interface ClientComplaintsFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    clientId: any;

    document: any;
    form: any;
    deals: any[];
    deals_deleted: any[];
    schema: any;

    savedReasons: any[];

    fileList: any[];

    meta: any;
}

const state: ClientComplaintsFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    clientId: null,

    document: null,
    form: null,
    deals: [],
    deals_deleted: [],
    schema: null,

    savedReasons: [],

    fileList: [],

    meta: {},
};

const initState: ClientComplaintsFormState = cloneDeep(state);

const COMPLIANT_SCHEMA = 'COMPLIANT_SCHEMA';
const GET_COMPLIANT = 'GET_COMPLIANT';
const GET_COMPLIANTS_DEALS = 'GET_COMPLIANTS_DEALS';
const GET_LINKED_REASONS = 'GET_LINKED_REASONS';

const COMPLINT_REASON_LIST_OP = 'COUNTERPARTY_LIST_OP';
const DOCUMENTS_SAVE_OP = 'tb-docform-save';

export const actions: ActionTree<ClientComplaintsFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        state.clientId = params.clientId;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(GET_COMPLIANT)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
                vm.$q(GET_LINKED_REASONS)
                    .select()
                    .from('complaint_complaint_reasons')
                    .fileds({
                        id: 'complaint_complaint_reasons.id',
                        complaint_id: 'complaint_complaint_reasons.complaint_id',
                        complaint_reason_id: 'complaint_complaint_reasons.complaint_reason_id',
                    })
                    .where((_: any) => {
                        return _.eq('complaint_complaint_reasons.complaint_id', _.const(params.id));
                    }),
                vm.$q(GET_COMPLIANTS_DEALS)
                    .select()
                    .from('complaints_deals')
                    .fileds({
                        id: 'complaints_deals.id',
                        deal_name:  'deals.name',
                        deal_date:  'deals.expected_delivery_date',
                        address: 'delivery_points.address',
                    })
                    .innerJoin('deals', (_: any) => {
                        return _.and([
                            _.eq("complaints_deals.deal_id", "deals.id"),
                            ...(params.clientId ? [_.eq("deals.client_id", _.const(params.clientId))] : [])
                        ])
                    })
                    .leftJoin('delivery_points', (_: any) => {
                        return _.eq('delivery_points.id', 'deals.delivery_point_id')
                    })
                    .where((_: any) => {
                        return _.eq('complaints_deals.complaint_id', _.const(params.id));
                    })
            ]).then((result: ServerResult) => {
                const complaint = result.getOperation(GET_COMPLIANT).getContent();

                commit('SET_FORM_DATA', {
                    document: complaint.document,
                    schema: complaint.schema,
                    reasons: result.getOperation(GET_LINKED_REASONS).getContent().documents,
                    deals: result.getOperation(GET_COMPLIANTS_DEALS).getContent().documents,
                });
                // commit('SET_COMPLIANTS_DATA', result.getOperation(COMPLINT_REASON_LIST_OP).getContent());

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        state.clientId = initParams.clientId;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(COMPLIANT_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:complaints`
                    }),
                ...(initParams.dealIds ? [vm.$q(GET_COMPLIANTS_DEALS)
                    .select()
                    .from('deals')
                    .fileds({
                        deal_id:  'deals.id',
                        deal_name:  'deals.name',
                        deal_date:  'deals.expected_delivery_date',
                        address: 'delivery_points.address',
                    })
                    .leftJoin('delivery_points', (_: any) => {
                        return _.eq('delivery_points.id', 'deals.delivery_point_id')
                    })
                    .where((_: any) => {
                        return _.in('deals.id', _.const(initParams.dealIds)); 
                    })] : [])
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(COMPLIANT_SCHEMA).getContent(),
                    deals: result.getOperation(GET_COMPLIANTS_DEALS) ? result.getOperation(GET_COMPLIANTS_DEALS).getContent().documents: [], 
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ state, commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            const $form = {
                ...cloneDeep(normalizedForm),
                client_id: state.clientId
            };
            const selectedReasonIds = state.form.reason_ids;

            if (documentId) {
                saveQuery = 
                    vm.$q(DOCUMENTS_SAVE_OP)
                        .update(documentId, $form)
                        .createManyRelation('complaint_complaint_reasons', 'complaint_id', (createRelated: any) => {
                            const oldReasons = state.savedReasons.map((doc: any) => doc.complaint_reason_id);
                            return selectedReasonIds.filter((value: any) => !oldReasons.includes(value)).map((link: any, index: number) => {
                                return createRelated({
                                    complaint_reason_id: link
                                });
                            });
                        })
                        .deleteManyRelation('complaint_complaint_reasons', 'complaint_id', (deleteRelated: any) => {
                            return state.savedReasons.filter((value: any) => !selectedReasonIds.includes(value.complaint_reason_id)).map((link, index) => {
                                return deleteRelated(link);
                            });
                        })
                        .createManyRelation('complaints_deals', 'complaint_id', (createRelated: any) => {
                            return state.deals.filter((item: any) => !item.id || (typeof (item.id) !== 'string')).map((item: any) => createRelated({deal_id: item.deal_id}));
                        })
                        .deleteManyRelation('complaints_deals', 'complaint_id', (deleteRelated: any) => {
                            return state.deals_deleted.map((relId: any) => deleteRelated({
                                id: relId
                            }));
                        })
            } else {
                saveQuery = 
                    vm.$q(DOCUMENTS_SAVE_OP).create('s:complaints', $form)
                        .withManyRelation('complaint_complaint_reasons', 'complaint_id', (createRelated: any) => {
                            return selectedReasonIds.map((id: string, index: number) => {
                                return createRelated({
                                    complaint_reason_id: id
                                });
                            });
                        })
                        .withManyRelation('complaints_deals', 'complaint_id', (createRelated: any) => {
                            return state.deals.map((item: any) => createRelated({deal_id: item.deal_id}));
                        })
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const saveOperation = result.getOperation(DOCUMENTS_SAVE_OP);

                if (saveOperation.hasError()) {
                    console.log('error', saveOperation.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', saveOperation.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(saveOperation.getContent());
                }
            })
            .catch((error: any) => {
                console.log('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    addAttachments({state}, newValue) {
        state.form.attachments = [
            newValue,
            ...(state.form.attachments ? state.form.attachments : []),
        ]
    },

    addDeal({state}, {deal}) {
        state.deals.push({
            deal_id: deal.id,
            deal_date: deal.deal_date,
            deal_name: deal.label_secondary,
            address: deal.label
        })
    },
    delDeal({state}, {index}) {
        if (state.deals[index]){
            if (state.deals[index].id) {
                state.deals_deleted.push(state.deals[index].id)
            }
        
            state.deals.splice(index, 1);
        }
    },

};

export const mutations: MutationTree<ClientComplaintsFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {document, schema, reasons, deals}) {
        // console.log('document, schema, reasons', document, schema, reasons)
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);
        state.savedReasons = reasons;
        state.deals = deals;
        
        state.form = {
            ...generateFormObject(schema, state.document),
            reason_ids: state.savedReasons.map((reason: any) => {
                return reason.complaint_reason_id
            })
        };
    },

    GENERATE_FORM_DATA(state, {schema, initParams, deals}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);
        state.deals = deals;

        state.form = {
            ...generateFormObject(schema, initParams),
            reason_ids: []
        };
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;
        
        state.form = {
            ...generateFormObject(schema, state.document),
            reason_ids: []
        }
    },

    CHANGE_VALUE(state: ClientComplaintsFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: ClientComplaintsFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_client_complaint_details_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
